import React, { useEffect, useState } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import StatusBadge from "@/components/StatusBadge";
import { toast } from "sonner";

export default function AdminQuotes() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(null);
  const [f, setF] = useState({ status: "in_review", quoted_price: "", quoted_notes: "" });

  const load = () => api.get("/admin/quotes").then((r) => setItems(r.data)).catch(()=>{});
  useEffect(() => { load(); }, []);

  const openDialog = (q) => {
    setOpen(q);
    setF({ status: q.status, quoted_price: q.quoted_price ?? "", quoted_notes: q.quoted_notes ?? "" });
  };

  const save = async () => {
    try {
      await api.patch(`/admin/quotes/${open.id}`, {
        status: f.status,
        quoted_price: f.quoted_price === "" ? null : parseFloat(f.quoted_price),
        quoted_notes: f.quoted_notes || null,
      });
      toast.success("Quote updated"); setOpen(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <DashboardLayout title="Quote requests">
      <Card>
        <CardHeader><CardTitle className="font-heading tracking-tight">{items.length} request{items.length===1?"":"s"}</CardTitle></CardHeader>
        <CardContent>
          {items.length === 0 ? (
            <div className="py-10 text-center text-muted-foreground text-sm" data-testid="admin-quotes-empty">No quote requests.</div>
          ) : (
            <Table data-testid="admin-quotes-table">
              <TableHeader><TableRow><TableHead>Buyer</TableHead><TableHead>Product</TableHead><TableHead>Qty</TableHead><TableHead>Target</TableHead><TableHead>Quoted</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Action</TableHead></TableRow></TableHeader>
              <TableBody>
                {items.map((q) => (
                  <TableRow key={q.id} data-testid={`admin-quote-${q.id}`}>
                    <TableCell><div className="font-medium text-secondary">{q.buyer?.company_name || q.buyer?.name}</div><div className="text-xs text-muted-foreground">{q.buyer?.email}</div></TableCell>
                    <TableCell>{q.product_name}</TableCell>
                    <TableCell>{q.quantity}</TableCell>
                    <TableCell>{q.target_price || "—"}</TableCell>
                    <TableCell>{q.quoted_price || "—"}</TableCell>
                    <TableCell><StatusBadge status={q.status} /></TableCell>
                    <TableCell className="text-right"><Button size="sm" onClick={() => openDialog(q)} data-testid={`process-${q.id}`}>Process</Button></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
      <Dialog open={!!open} onOpenChange={(v) => !v && setOpen(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading tracking-tight">Process quote</DialogTitle></DialogHeader>
          {open && (
            <div className="space-y-3">
              <div className="text-sm text-muted-foreground">{open.product_name} · Qty {open.quantity} · Buyer {open.buyer?.name}</div>
              <div>
                <Label>Status</Label>
                <select data-testid="qs-status" className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm" value={f.status} onChange={(e)=>setF({...f,status:e.target.value})}>
                  <option value="new">New</option>
                  <option value="in_review">In review</option>
                  <option value="quoted">Quoted</option>
                  <option value="closed">Closed</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>
              <div><Label>Quoted price</Label><Input data-testid="qs-price" type="number" step="0.01" value={f.quoted_price} onChange={(e)=>setF({...f,quoted_price:e.target.value})}/></div>
              <div><Label>Notes to buyer</Label><Textarea data-testid="qs-notes" rows={3} value={f.quoted_notes} onChange={(e)=>setF({...f,quoted_notes:e.target.value})}/></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={()=>setOpen(null)}>Cancel</Button>
            <Button data-testid="qs-save" onClick={save}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
